import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CalendarX2, CalendarCheck2, LogOut, Loader2, RefreshCw } from "lucide-react";
import OwnerCalendarStrip from "@/components/OwnerCalendarStrip";
import OwnerBookingCard from "@/components/OwnerBookingCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
function formatDetail(detail) {
  if (detail == null) return "Something went wrong — try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg).filter(Boolean).join(", ");
  return "Something went wrong — try again.";
}

const SOURCE_CHIPS = {
  deposit: { label: "Paid booking", cls: "bg-gold/15 text-gold border-gold/40" },
  manual: { label: "Owner block", cls: "bg-rose/10 text-rose border-rose/30" },
};

export default function OwnerDashboard({ user, onLogout }) {
  const [enquiries, setEnquiries] = useState(null);
  const [blocks, setBlocks] = useState(null);
  const [newDate, setNewDate] = useState("");
  const [blocking, setBlocking] = useState(false);

  const load = async () => {
    try {
      const [e, b] = await Promise.all([
        axios.get(`${API}/owner/enquiries`, { withCredentials: true }),
        axios.get(`${API}/owner/blocks`, { withCredentials: true }),
      ]);
      setEnquiries(e.data.enquiries);
      setBlocks(b.data.blocks);
    } catch {
      toast.error("Could not load dashboard data — refresh the page.");
    }
  };

  useEffect(() => {
    load();
  }, []);

  const blockDate = async (e) => {
    e.preventDefault();
    if (!newDate) return;
    setBlocking(true);
    try {
      await axios.post(`${API}/owner/blocks`, { date: newDate }, { withCredentials: true });
      toast.success(`${newDate} is now blocked.`);
      setNewDate("");
      await load();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Could not block that date.");
    } finally {
      setBlocking(false);
    }
  };

  const unblockDate = async (date) => {
    try {
      await axios.post(`${API}/owner/unblocks`, { date }, { withCredentials: true });
      toast.success(`${date} is open again.`);
      await load();
    } catch (err) {
      toast.error(formatDetail(err?.response?.data?.detail) || "Could not open that date.");
    }
  };

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    onLogout();
  };

  return (
    <section data-testid="owner-dashboard" className="grain min-h-screen bg-espresso-deep px-5 py-14 sm:px-8">
      <div className="mx-auto max-w-5xl">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <span className="eyebrow text-gold">Owner's desk</span>
            <h1 className="mt-3 font-display text-4xl text-cream sm:text-5xl">
              Bookings & <em className="italic text-rose">availability</em>
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              data-testid="owner-refresh-btn"
              onClick={load}
              className="flex items-center gap-2 rounded-full border border-cream/25 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-cream transition-colors hover:border-gold hover:text-gold"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Refresh
            </button>
            <button
              data-testid="owner-logout"
              onClick={logout}
              className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.16em] text-espresso transition-all hover:bg-cream"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign out
            </button>
          </div>
        </div>

        <div className="mt-12 rounded-3xl border border-cream/15 bg-white/[0.03] p-6 sm:p-8" data-testid="owner-blocks-panel">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-cream/10 text-gold">
              <CalendarX2 className="h-4 w-4" />
            </span>
            <h2 className="font-display text-2xl text-cream">Block or open a date</h2>
          </div>
          <div className="mt-5 grid gap-8 lg:grid-cols-2">
            <OwnerCalendarStrip blocks={blocks} />
            <div>
          <form onSubmit={blockDate} className="flex flex-col gap-3 sm:flex-row">
            <input
              data-testid="owner-block-date-input"
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              className="flex-1 rounded-xl border border-cream/20 bg-white/[0.04] px-4 py-3 text-sm text-cream outline-none transition-colors focus:border-gold [color-scheme:dark]"
              aria-label="Date to block"
            />
            <button
              data-testid="owner-block-button"
              disabled={blocking || !newDate}
              className="flex items-center justify-center gap-2 rounded-full bg-gold px-7 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-espresso transition-all hover:bg-cream disabled:opacity-50"
            >
              {blocking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Block this date"}
            </button>
          </form>

          <ul className="mt-6 grid gap-2 sm:grid-cols-2" data-testid="owner-blocks-list">
            {(blocks || []).map((b) => {
              const chip = SOURCE_CHIPS[b.source] || { label: "Blocked", cls: "bg-cream/10 text-cream/70 border-cream/25" };
              return (
                <li
                  key={b.date}
                  data-testid={`owner-block-row-${b.date}`}
                  className="flex items-center justify-between gap-3 rounded-xl border border-cream/15 bg-white/[0.02] px-4 py-3"
                >
                  <span className="flex items-center gap-2 text-sm text-cream">
                    <CalendarCheck2 className="h-4 w-4 text-gold" /> {b.date}
                  </span>
                  <span className="flex items-center gap-2">
                    <span className={`rounded-full border px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.1em] ${chip.cls}`}>
                      {chip.label}
                    </span>
                    {b.source !== "deposit" && (
                      <button
                        data-testid={`owner-unblock-${b.date}`}
                        onClick={() => unblockDate(b.date)}
                        className="rounded-full border border-cream/25 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-cream/70 transition-colors hover:border-rose hover:text-rose"
                      >
                        Open
                      </button>
                    )}
                  </span>
                </li>
              );
            })}
            {blocks && blocks.length === 0 && (
              <li className="text-sm text-cream/50">No blocked dates — every date is open.</li>
            )}
          </ul>
            </div>
          </div>
        </div>

        <div className="mt-10" data-testid="owner-bookings-panel">
          <h2 className="font-display text-2xl text-cream">
            Bookings & enquiries <span className="text-cream/40">({enquiries ? enquiries.length : "…"})</span>
          </h2>
          <div className="mt-5 grid gap-3" data-testid="owner-bookings-list">
            {(enquiries || []).map((enq) => (
              <OwnerBookingCard key={enq.reference} enq={enq} onChanged={load} />
            ))}
            {enquiries && enquiries.length === 0 && (
              <p className="text-sm text-cream/50" data-testid="owner-bookings-empty">
                No bookings or enquiries yet — they'll appear here the moment one comes in.
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}